import type { Task } from '@timato/shared';
import { useOpenTasks, useProjectMap, useRecentTaskIds } from '@/data/hooks';

/** The list is a shortlist, not a browser: Projects is where you go to see everything. */
export const SUGGESTION_LIMIT = 8;

/**
 * Open tasks for a goal field (§3.2): the ones whose name or project matches the query, and carry the label when one
 * is given. The given project's tasks lead, then whatever was worked on most recently. Sorting before the cap is the
 * point: with a hundred open tasks the eight you see are the eight you are likely to want, rather than the eight that
 * happen to sort first. `sort` is stable, so tasks never tracked keep their manual order.
 *
 * `tasks` is undefined while it reads, so a caller can stay silent rather than announce "no tasks" a frame early.
 */
export function useTaskSuggestions(query: string, projectId: string | null, labelId: string | null = null) {
  const tasks = useOpenTasks();
  const recent = useRecentTaskIds();
  const projects = useProjectMap();
  const q = query.trim().toLowerCase();
  const found = (tasks ?? []).filter((t) => (!labelId || t.labelIds.includes(labelId)) && (!q || t.name.toLowerCase().includes(q) || !!projects.get(t.projectId)?.name.toLowerCase().includes(q)));
  const inProject = (t: Task) => !!projectId && t.projectId === projectId;
  const lastWorked = (t: Task) => recent.get(t.id) ?? Number.MAX_SAFE_INTEGER;
  const matches = [...found].sort((a, b) => Number(inProject(b)) - Number(inProject(a)) || lastWorked(a) - lastWorked(b)).slice(0, SUGGESTION_LIMIT);
  /** Whether a task by exactly this name is on the list, so there is nothing to create. */
  const exact = matches.some((t) => t.name.trim().toLowerCase() === q);
  return { tasks, found, matches, inProject, exact };
}
